import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api.js";
import { KeyRound, Info, Eye, Lock, Search, Download, Users as UsersIcon, Edit3, Shield } from "lucide-react";
import { UserDialog } from "./UsersTab.jsx";

const LEVEL_STYLES = {
  write:  { label: "Escrita", cls: "bg-[#D4EDDA] text-[#155724]" },
  read:   { label: "Leitura", cls: "bg-[#E7F3FF] text-[#0B5ED7]" },
  none:   { label: "Sem acesso", cls: "bg-[#F4F6F8] text-[#94A3B8]" },
};

export default function PermissionsMatrixPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [simulateRole, setSimulateRole] = useState("");
  const [view, setView] = useState("roles"); // "roles" | "users"

  // User-matrix state
  const [users, setUsers] = useState([]);
  const [enums, setEnums] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [showOnlyExtras, setShowOnlyExtras] = useState(false);

  const loadUsers = async () => {
    const [u, e] = await Promise.all([api.get("/users"), api.get("/enums")]);
    setUsers(u.data);
    setEnums(e.data);
  };

  useEffect(() => {
    api.get("/admin/permissions-matrix")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Falha a carregar matriz"));
    loadUsers();
  }, []);

  const preview = useMemo(() => {
    if (!data || !simulateRole) return null;
    const roleMatrix = data.matrix[simulateRole] || {};
    const write = data.modules.filter((m) => roleMatrix[m.key] === "write");
    const read = data.modules.filter((m) => roleMatrix[m.key] === "read");
    const none = data.modules.filter((m) => roleMatrix[m.key] === "none");
    return { write, read, none };
  }, [data, simulateRole]);

  // Build user × module matrix (effective access + source: role_default | extra | none)
  const userMatrix = useMemo(() => {
    if (!data) return [];
    const defaultModulesByRole = Object.fromEntries(
      data.roles.map((r) => [r.key, new Set(
        data.modules.filter((m) => (data.matrix[r.key] || {})[m.key] !== "none").map((m) => m.key),
      )])
    );
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => (filterRole ? u.role === filterRole : true))
      .filter((u) => !q || (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)))
      .filter((u) => !showOnlyExtras || (u.assigned_modules && u.assigned_modules.length > 0))
      .filter((u) => {
        if (!filterModule) return true;
        return (u.effective_modules || []).includes(filterModule);
      })
      .map((u) => {
        const defaults = defaultModulesByRole[u.role] || new Set();
        const extras = new Set(u.assigned_modules || []);
        const cells = Object.fromEntries(
          data.modules.map((m) => {
            if (defaults.has(m.key)) return [m.key, "default"];
            if (extras.has(m.key)) return [m.key, "extra"];
            return [m.key, "none"];
          })
        );
        return { user: u, cells };
      });
  }, [data, users, search, filterRole, filterModule, showOnlyExtras]);

  const exportUserMatrixCsv = () => {
    if (!data || userMatrix.length === 0) return;
    const header = ["Nome", "Email", "Perfil", "Filial", ...data.modules.map((m) => m.label)];
    const rows = userMatrix.map(({ user, cells }) => [
      user.name,
      user.email,
      (data.roles.find((r) => r.key === user.role) || {}).label || user.role,
      user.branch || "",
      ...data.modules.map((m) => {
        const v = cells[m.key];
        return v === "default" ? "Perfil" : v === "extra" ? "Extra" : "—";
      }),
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `matriz-permissoes-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  if (err) return <div className="p-4 text-sm text-[#721C24] bg-[#F8D7DA] rounded-sm">{err}</div>;
  if (!data) return <div className="p-4 text-sm text-[#6C757D]">A carregar…</div>;

  return (
    <div className="space-y-4" data-testid="permissions-matrix-panel">
      <div>
        <h2 className="font-heading text-xl font-bold text-[#1A1A2E] flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#1F3864]" /> Permissões e Acessos
        </h2>
        <p className="text-xs text-[#6C757D] mt-0.5 max-w-3xl">
          Vê as permissões por perfil (defaults) ou por utilizador individual (defaults + extras concedidos pelo admin). Clica em qualquer linha de utilizador para editar os módulos extra.
        </p>
      </div>

      {/* View switcher */}
      <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-sm p-1 w-fit" data-testid="perm-view-switch">
        <button
          onClick={() => setView("roles")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${view === "roles" ? "bg-[#1F3864] text-white" : "text-[#495057] hover:bg-[#F4F6F8]"}`}
          data-testid="perm-view-roles"
        >
          <Shield className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> Por perfil
        </button>
        <button
          onClick={() => setView("users")}
          className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${view === "users" ? "bg-[#1F3864] text-white" : "text-[#495057] hover:bg-[#F4F6F8]"}`}
          data-testid="perm-view-users"
        >
          <UsersIcon className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> Por utilizador <span className="ml-1 opacity-70">({users.length})</span>
        </button>
      </div>

      {view === "roles" && (
        <RoleMatrixSection
          data={data}
          preview={preview}
          simulateRole={simulateRole}
          setSimulateRole={setSimulateRole}
        />
      )}

      {view === "users" && (
        <UserMatrixSection
          data={data}
          userMatrix={userMatrix}
          users={users}
          search={search}
          setSearch={setSearch}
          filterRole={filterRole}
          setFilterRole={setFilterRole}
          filterModule={filterModule}
          setFilterModule={setFilterModule}
          showOnlyExtras={showOnlyExtras}
          setShowOnlyExtras={setShowOnlyExtras}
          onEditUser={(u) => setEditing(u)}
          onExport={exportUserMatrixCsv}
        />
      )}

      {editing && enums && (
        <UserDialog
          enums={enums}
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadUsers(); }}
        />
      )}
    </div>
  );
}

// ----------------- Role matrix (existing) -----------------
function RoleMatrixSection({ data, preview, simulateRole, setSimulateRole }) {
  return (
    <>
      {data.note && (
        <div className="flex items-start gap-2 p-3 bg-[#E7F3FF] border border-[#0B5ED7]/20 rounded-sm text-xs text-[#0B5ED7]">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{data.note}</span>
        </div>
      )}

      <section className="bg-white border border-gray-200 rounded-sm p-4" data-testid="perm-simulator">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs font-semibold text-[#1A1A2E] flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5 text-[#1F3864]" /> Ver como…
          </label>
          <select
            value={simulateRole}
            onChange={(e) => setSimulateRole(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864] bg-white"
            data-testid="perm-simulator-select"
          >
            <option value="">— Escolhe um perfil para pré-visualizar acessos —</option>
            {data.roles.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
          {simulateRole && (
            <button
              onClick={() => setSimulateRole("")}
              className="text-xs text-[#6C757D] hover:text-[#1A1A2E] underline"
              data-testid="perm-simulator-clear"
            >
              Limpar
            </button>
          )}
        </div>
        {preview && (
          <div className="mt-3 grid md:grid-cols-3 gap-3 text-xs" data-testid="perm-simulator-preview">
            <PreviewGroup title="Pode editar" tone="green" items={preview.write} />
            <PreviewGroup title="Só leitura" tone="blue" items={preview.read} />
            <PreviewGroup title="Sem acesso" tone="grey" items={preview.none} />
          </div>
        )}
      </section>

      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
              <th className="sticky left-0 bg-[#F4F6F8] px-3 py-2 text-left border-r border-gray-200 min-w-[180px]">Perfil</th>
              {data.modules.map((m) => (
                <th key={m.key} className="px-2 py-2 text-center whitespace-nowrap">{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.roles.map((r) => (
              <tr key={r.key} className="border-t border-gray-100" data-testid={`perm-row-${r.key}`}>
                <td className="sticky left-0 bg-white px-3 py-2 border-r border-gray-200 text-xs">
                  <div className="font-semibold text-[#1A1A2E]">{r.label}</div>
                  <div className="text-[10px] font-mono text-[#94A3B8]">{r.key}</div>
                </td>
                {data.modules.map((m) => {
                  const lvl = (data.matrix[r.key] || {})[m.key] || "none";
                  const s = LEVEL_STYLES[lvl];
                  return (
                    <td key={m.key} className="px-2 py-2 text-center" data-testid={`perm-cell-${r.key}-${m.key}`}>
                      <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${s.cls}`}>{s.label}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-[#6C757D]">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#D4EDDA] rounded-sm"/> Escrita — pode criar/editar</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#E7F3FF] rounded-sm"/> Leitura — só pode ver</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#F4F6F8] border border-gray-200 rounded-sm"/> Sem acesso</span>
      </div>
    </>
  );
}

// ----------------- User × Module matrix (new) -----------------
function UserMatrixSection({
  data,
  userMatrix,
  users,
  search, setSearch,
  filterRole, setFilterRole,
  filterModule, setFilterModule,
  showOnlyExtras, setShowOnlyExtras,
  onEditUser,
  onExport,
}) {
  const extrasCount = users.filter((u) => (u.assigned_modules || []).length > 0).length;

  return (
    <>
      {/* Filters */}
      <section className="bg-white border border-gray-200 rounded-sm p-3" data-testid="user-matrix-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Procurar por nome ou email…"
              className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-[#1F3864]"
              data-testid="user-matrix-search"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864] bg-white"
            data-testid="user-matrix-filter-role"
          >
            <option value="">Todos os perfis</option>
            {data.roles.map((r) => (<option key={r.key} value={r.key}>{r.label}</option>))}
          </select>
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864] bg-white"
            data-testid="user-matrix-filter-module"
          >
            <option value="">Todos os módulos</option>
            {data.modules.map((m) => (<option key={m.key} value={m.key}>Com acesso a: {m.label}</option>))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-[#495057] px-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyExtras}
              onChange={(e) => setShowOnlyExtras(e.target.checked)}
              className="accent-[#1F3864]"
              data-testid="user-matrix-only-extras"
            />
            Só com extras ({extrasCount})
          </label>
          <button
            onClick={onExport}
            className="ml-auto flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded-sm hover:bg-[#F4F6F8]"
            data-testid="user-matrix-export"
          >
            <Download className="w-3 h-3" /> Exportar CSV
          </button>
        </div>
      </section>

      <div className="text-xs text-[#6C757D]">
        A mostrar <b>{userMatrix.length}</b> de {users.length} utilizadores. Clica numa linha para editar os módulos extra.
      </div>

      {/* Matrix table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="user-matrix-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
              <th className="sticky left-0 bg-[#F4F6F8] px-3 py-2 text-left border-r border-gray-200 min-w-[220px]">Utilizador</th>
              {data.modules.map((m) => (
                <th key={m.key} className="px-2 py-2 text-center whitespace-nowrap" title={m.label}>
                  {m.label.length > 14 ? m.label.slice(0, 12) + "…" : m.label}
                </th>
              ))}
              <th className="px-2 py-2 text-right w-[80px]">Acção</th>
            </tr>
          </thead>
          <tbody>
            {userMatrix.length === 0 && (
              <tr>
                <td colSpan={data.modules.length + 2} className="px-3 py-6 text-center text-xs text-[#94A3B8]">
                  Nenhum utilizador corresponde aos filtros aplicados.
                </td>
              </tr>
            )}
            {userMatrix.map(({ user, cells }) => {
              const roleLabel = (data.roles.find((r) => r.key === user.role) || {}).label || user.role;
              const extraCount = (user.assigned_modules || []).length;
              return (
                <tr
                  key={user.id}
                  className={`border-t border-gray-100 hover:bg-[#FFFBEA] cursor-pointer transition-colors ${user.deactivated ? "opacity-50" : ""}`}
                  onClick={() => onEditUser(user)}
                  data-testid={`user-matrix-row-${user.id}`}
                >
                  <td className="sticky left-0 bg-white hover:bg-[#FFFBEA] px-3 py-2 border-r border-gray-200">
                    <div className="font-semibold text-[#1A1A2E] text-xs flex items-center gap-1">
                      {user.name}
                      {extraCount > 0 && (
                        <span className="inline-block text-[9px] px-1 py-0.5 bg-[#FFF3CD] text-[#856404] font-bold rounded-sm">
                          +{extraCount}
                        </span>
                      )}
                      {user.deactivated && (
                        <span className="inline-block text-[9px] px-1 py-0.5 bg-[#E2E8F0] text-[#495057] rounded-sm">
                          INACTIVO
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#6C757D] mt-0.5">{roleLabel} · {user.branch || "—"}</div>
                  </td>
                  {data.modules.map((m) => {
                    const kind = cells[m.key];
                    return (
                      <td key={m.key} className="px-2 py-2 text-center" data-testid={`user-matrix-cell-${user.id}-${m.key}`}>
                        {kind === "default" && (
                          <span
                            title="Acesso por defeito do perfil"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#D4EDDA] text-[#155724] text-xs font-bold"
                          >✓</span>
                        )}
                        {kind === "extra" && (
                          <span
                            title="Acesso extra concedido manualmente"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#FFF3CD] text-[#856404] text-xs font-bold"
                          >+</span>
                        )}
                        {kind === "none" && (
                          <span
                            title="Sem acesso"
                            className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#F4F6F8] text-[#CBD5E1] text-xs"
                          >·</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); onEditUser(user); }}
                      className="inline-flex items-center gap-1 text-[11px] text-[#1F3864] hover:underline"
                      data-testid={`user-matrix-edit-${user.id}`}
                    >
                      <Edit3 className="w-3 h-3" /> Editar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px] text-[#6C757D]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 bg-[#D4EDDA] text-[#155724] text-[10px] font-bold rounded-sm">✓</span>
          Default do perfil
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 bg-[#FFF3CD] text-[#856404] text-[10px] font-bold rounded-sm">+</span>
          Extra concedido pelo admin
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-4 h-4 bg-[#F4F6F8] text-[#CBD5E1] text-[10px] rounded-sm">·</span>
          Sem acesso
        </span>
      </div>
    </>
  );
}

function PreviewGroup({ title, tone, items }) {
  const bg = tone === "green" ? "bg-[#F0FDF4] border-[#D4EDDA]" : tone === "blue" ? "bg-[#F0F9FF] border-[#B5DAFF]" : "bg-[#F4F6F8] border-gray-200";
  const chip = tone === "green" ? "bg-[#D4EDDA] text-[#155724]" : tone === "blue" ? "bg-[#E7F3FF] text-[#0B5ED7]" : "bg-white text-[#94A3B8] border border-gray-200";
  const Icon = tone === "grey" ? Lock : null;
  return (
    <div className={`border rounded-sm p-3 ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D] font-semibold mb-2 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {title} <span className="text-[#94A3B8]">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-[#94A3B8] italic">nenhum</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.map((m) => (
            <span key={m.key} className={`inline-block px-1.5 py-0.5 text-[10px] rounded-sm ${chip}`}>{m.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}
